import { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';

import { useAuth } from '../lib/auth/AuthProvider';
import { getKitBlob } from '../lib/recovery/recoveryApi';
import {
  isWellFormedKit, formatKit, normalizeKit,
} from '../lib/recovery/kitFormat';
import { unwrapKeyWithKit, writeOwnerPrivateKeyBytes } from '../lib/recovery/recoveryKit';
import './recovery.css';

/**
 * "I have my kit" — restoring the owner's key onto this device.
 *
 * An important limit, stated plainly on the screen rather than discovered:
 * the kit restores the KEY, not the sign-in. Getting a session on a new
 * device is the passkey's job. So this screen is for someone who can sign in
 * — their passkey synced, or they finished a recovery — but whose new device
 * has no key and therefore cannot read their own replies or review their own
 * recordings.
 *
 * A wrong kit fails locally, in this browser, against the AES-GCM tag. There
 * is no server round-trip that could tell anyone whether a guess was close,
 * and no attempt counter to exhaust, because there is nothing here to guess
 * at remotely.
 */
export default function RecoverWithKitScreen() {
  const { api, user, status } = useAuth();
  const navigate = useNavigate();
  const [entry, setEntry] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [restored, setRestored] = useState(false);

  const wellFormed = isWellFormedKit(entry);

  async function restore() {
    setBusy(true);
    setError('');
    try {
      const email = user?.email;
      if (email === undefined) throw new Error('Sign in first, then use your kit.');

      const blob = await getKitBlob(api);
      const bytes = await unwrapKeyWithKit(blob, entry);
      await writeOwnerPrivateKeyBytes(email, bytes);
      setRestored(true);
    } catch (e) {
      // Deliberately one message for both "wrong kit" and "corrupt blob". The
      // member can only do one thing about either, and distinguishing them
      // tells an attacker which half they got right.
      const msg = e instanceof Error && /No Recovery Kit/i.test(e.message)
        ? 'There is no kit stored for this account.'
        : 'That kit did not open your key. Check the characters and try again.';
      setError(msg);
    } finally {
      setBusy(false);
    }
  }

  if (status !== 'authed') {
    return (
      <section className="auth recovery">
        <p className="vault__kicker">Almost</p>
        <h1 className="vault__title">Sign in first</h1>
        <p className="vault__sub">
          Your kit restores your key onto this device — it does not sign you in. Sign in first,
          then come back here and enter it.
        </p>
        <p className="vault__sub">
          If you cannot sign in at all because this device does not recognise you, start a recovery
          instead.
        </p>
        <div className="recovery__actions">
          <Link className="la-btn-primary" to="/signin">Sign in</Link>
          <Link className="la-btn-secondary" to="/recover">Start a recovery</Link>
        </div>
      </section>
    );
  }

  if (restored) {
    return (
      <section className="auth recovery">
        <p className="vault__kicker">Done</p>
        <h1 className="vault__title">This device has your key</h1>
        <p className="vault__sub">
          You can read your replies and review your recordings on this device now, exactly as you
          could on your old one.
        </p>
        <p className="vault__sub">
          Your kit still works and has not changed. If you think anyone else has seen it, make a
          new one — the old characters stop working the moment the new ones appear.
        </p>
        <div className="recovery__actions">
          <button type="button" className="la-btn-primary" onClick={() => navigate('/')}>
            Go to my vault
          </button>
          <Link className="la-btn-secondary" to="/you/recovery-kit">Make a new kit</Link>
        </div>
      </section>
    );
  }

  return (
    <section className="auth recovery">
      <p className="vault__kicker">Your Recovery Kit</p>
      <h1 className="vault__title">Enter your twenty-four characters</h1>
      <p className="vault__sub">
        Type them as they appear on your kit. Dashes, spaces and capitals do not matter.
      </p>

      <label className="auth__field">
        <span className="auth__label">Your kit</span>
        <input
          className="auth__input recovery__input"
          value={entry}
          autoCapitalize="characters"
          autoComplete="off"
          spellCheck={false}
          placeholder="ABCD-EFGH-JKMN-PQRS-TVWX-YZ01"
          onChange={(e) => { setEntry(e.target.value); setError(''); }}
        />
      </label>
      {entry !== '' && !wellFormed && (
        <p className="vault__sub recovery__count">
          {normalizeKit(entry).length} of 24 characters.
        </p>
      )}
      {wellFormed && <p className="vault__sub recovery__count">Reads as {formatKit(normalizeKit(entry))}</p>}
      {error !== '' && <p className="auth__alert" role="alert">{error}</p>}

      <button type="button" className="la-btn-primary" onClick={() => void restore()} disabled={busy || !wellFormed}>
        {busy ? 'Opening…' : 'Restore my key'}
      </button>

      <p className="recovery__reassure">
        This happens in this browser. Your kit is never sent to us — we could not open it if it
        were.
      </p>
      <p className="recovery__back"><Link to="/recover">I do not have my kit</Link></p>
    </section>
  );
}
